import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import AdminSideBar from "../../common/AdminSideBar/AdminSideBar";
import { FiPlus, FiX, FiTag, FiTrash2, FiFilter } from "react-icons/fi";

const API_URL = process.env.REACT_APP_API_URL;

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
  "Content-Type": "application/json",
});

const STATUS_STYLE = {
  active:      "bg-green-100 text-green-700",
  pending:     "bg-yellow-100 text-yellow-700",
  used:        "bg-blue-100 text-blue-700",
  expired:     "bg-gray-100 text-gray-500",
  inactive:    "bg-red-100 text-red-600",
  deactivated: "bg-red-100 text-red-600",
};

const deriveStatus = (c) => {
  if (c.status) return c.status;
  if (c.isActive === false) return "inactive";
  if (c.validUntil && new Date(c.validUntil) < new Date()) return "expired";
  if (c.usageLimit && (c.usedCount ?? 0) >= c.usageLimit) return "used";
  return "active";
};

const fmtDate = (d) => {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

const EMPTY_FORM = {
  code: "",
  discountType: "percent",
  discountValue: "",
  validFrom: "",
  validUntil: "",
  usageLimit: "",
  perUserLimit: "1",
  allTherapists: true,
  therapistIds: [],
};

const FILTERS = [
  { key: "all",     label: "All" },
  { key: "active",  label: "Active" },
  { key: "pending", label: "Pending" },
  { key: "used",    label: "Used" },
];

const AdminCoupons = () => {
  const [coupons, setCoupons]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [statusFilter, setFilter]   = useState("all");
  const [showModal, setShowModal]   = useState(false);
  const [form, setForm]             = useState(EMPTY_FORM);
  const [therapists, setTherapists] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [deactivating, setDeactivating] = useState(null);

  const fetchCoupons = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: 100 });
      if (statusFilter === "active") params.set("active", "true");
      else if (statusFilter === "used" || statusFilter === "pending") params.set("status", statusFilter);

      const res = await axios.get(`${API_URL}/admin/coupons?${params}`, { headers: authHeaders() });
      setCoupons(res.data?.data || res.data?.coupons || res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load coupons");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { fetchCoupons(); }, [fetchCoupons]);

  const openModal = async () => {
    setForm(EMPTY_FORM);
    setShowModal(true);
    try {
      const res = await axios.get(`${API_URL}/therapists`, { headers: authHeaders() });
      setTherapists(res.data?.data || res.data || []);
    } catch {
      // non-critical
    }
  };

  const handleCreate = async () => {
    if (!form.code.trim() || !form.discountValue || !form.validUntil) {
      toast.error("Code, discount value, and valid until date are required.");
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        code: form.code.trim().toUpperCase(),
        discountType: form.discountType,
        discountValue: Number(form.discountValue),
        validUntil: new Date(form.validUntil).toISOString(),
        ...(form.validFrom && { validFrom: new Date(form.validFrom).toISOString() }),
        ...(form.usageLimit && { usageLimit: Number(form.usageLimit) }),
        perUserLimit: Number(form.perUserLimit) || 1,
        applicableTo: form.allTherapists
          ? { allTherapists: true }
          : { therapistIds: form.therapistIds },
      };
      await axios.post(`${API_URL}/admin/coupons`, body, { headers: authHeaders() });
      toast.success("Coupon created.");
      setShowModal(false);
      fetchCoupons();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to create coupon.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeactivate = async (id) => {
    if (!window.confirm("Deactivate this coupon?")) return;
    setDeactivating(id);
    try {
      await axios.delete(`${API_URL}/admin/coupons/${id}`, { headers: authHeaders() });
      toast.success("Coupon deactivated.");
      fetchCoupons();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to deactivate.");
    } finally {
      setDeactivating(null);
    }
  };

  const toggleTherapist = (id) =>
    setForm((f) => ({
      ...f,
      therapistIds: f.therapistIds.includes(id)
        ? f.therapistIds.filter((t) => t !== id)
        : [...f.therapistIds, id],
    }));

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-gray-100">
      <div className="md:shrink-0 md:mr-6">
        <AdminSideBar />
      </div>

      <div className="flex-1 p-4 md:p-6 lg:p-8">
        <div className="bg-white rounded-xl shadow-md p-6 max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold text-gray-900">Coupons</h1>
            <button
              onClick={openModal}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              <FiPlus className="w-4 h-4" />
              New Coupon
            </button>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-3 mb-6 items-center">
            <FiFilter className="text-gray-400 w-4 h-4" />
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
              {FILTERS.map((f) => (
                <button
                  key={f.key}
                  onClick={() => setFilter(f.key)}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                    statusFilter === f.key
                      ? "bg-white shadow text-gray-900"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <span className="ml-auto text-sm text-gray-400">{coupons.length} coupons</span>
          </div>

          {/* Table */}
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500" />
            </div>
          ) : coupons.length === 0 ? (
            <div className="text-center py-16 text-gray-400">No coupons found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-left text-gray-500 text-xs uppercase tracking-wide">
                    <th className="pb-3 pr-4 font-semibold">Code</th>
                    <th className="pb-3 pr-4 font-semibold">Type</th>
                    <th className="pb-3 pr-4 font-semibold">Value</th>
                    <th className="pb-3 pr-4 font-semibold">Valid Until</th>
                    <th className="pb-3 pr-4 font-semibold">Applies To</th>
                    <th className="pb-3 pr-4 font-semibold">Uses</th>
                    <th className="pb-3 pr-4 font-semibold">Status</th>
                    <th className="pb-3 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {coupons.map((c) => {
                    const status = deriveStatus(c);
                    const canDeactivate = status === "active" || status === "pending";
                    return (
                      <tr key={c._id} className="hover:bg-gray-50 transition-colors">
                        <td className="py-3 pr-4">
                          <div className="flex items-center gap-1.5">
                            <FiTag className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                            <span className="font-mono font-semibold text-gray-900">{c.code}</span>
                          </div>
                        </td>
                        <td className="py-3 pr-4">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            c.discountType === "percent"
                              ? "bg-purple-100 text-purple-700"
                              : "bg-orange-100 text-orange-700"
                          }`}>
                            {c.discountType === "percent" ? "%" : "£"}
                          </span>
                        </td>
                        <td className="py-3 pr-4 text-gray-700">
                          {c.discountType === "percent" ? `${c.discountValue}%` : `£${c.discountValue}`}
                        </td>
                        <td className="py-3 pr-4 text-gray-600">{fmtDate(c.validUntil)}</td>
                        <td className="py-3 pr-4 text-gray-600">
                          {c.applicableTo?.allTherapists
                            ? "All"
                            : `${c.applicableTo?.therapistIds?.length ?? 0} therapist(s)`}
                        </td>
                        <td className="py-3 pr-4 text-gray-600">
                          {c.usedCount ?? 0}
                          {c.usageLimit ? ` / ${c.usageLimit}` : ""}
                        </td>
                        <td className="py-3 pr-4">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLE[status] || "bg-gray-100 text-gray-600"}`}>
                            {status.charAt(0).toUpperCase() + status.slice(1)}
                          </span>
                        </td>
                        <td className="py-3">
                          {canDeactivate && (
                            <button
                              onClick={() => handleDeactivate(c._id)}
                              disabled={deactivating === c._id}
                              className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
                            >
                              <FiTrash2 className="w-3 h-3" />
                              {deactivating === c._id ? "..." : "Deactivate"}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Create Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold text-gray-900">New Coupon</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors">
                <FiX className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Code */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Code *</label>
                <input
                  type="text"
                  value={form.code}
                  onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
                  placeholder="SUMMER30"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-300"
                />
              </div>

              {/* Type + Value */}
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
                  <select
                    value={form.discountType}
                    onChange={(e) => setForm((f) => ({ ...f, discountType: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                  >
                    <option value="percent">Percentage (%)</option>
                    <option value="fixed">Fixed (£)</option>
                  </select>
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Value *</label>
                  <input
                    type="number"
                    min="0"
                    value={form.discountValue}
                    onChange={(e) => setForm((f) => ({ ...f, discountValue: e.target.value }))}
                    placeholder={form.discountType === "percent" ? "30" : "25"}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                  />
                </div>
              </div>

              {/* Valid From / Until */}
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Valid From</label>
                  <input
                    type="date"
                    value={form.validFrom}
                    onChange={(e) => setForm((f) => ({ ...f, validFrom: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Valid Until *</label>
                  <input
                    type="date"
                    value={form.validUntil}
                    onChange={(e) => setForm((f) => ({ ...f, validUntil: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                  />
                </div>
              </div>

              {/* Usage Limit / Per User */}
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Usage Limit{" "}
                    <span className="text-gray-400 font-normal text-xs">(empty = unlimited)</span>
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={form.usageLimit}
                    onChange={(e) => setForm((f) => ({ ...f, usageLimit: e.target.value }))}
                    placeholder="Unlimited"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Per User Limit</label>
                  <input
                    type="number"
                    min="1"
                    value={form.perUserLimit}
                    onChange={(e) => setForm((f) => ({ ...f, perUserLimit: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                  />
                </div>
              </div>

              {/* Applies To */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Applies To</label>
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer mb-3">
                  <input
                    type="checkbox"
                    checked={form.allTherapists}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, allTherapists: e.target.checked, therapistIds: [] }))
                    }
                    className="rounded"
                  />
                  All Therapists
                </label>

                {!form.allTherapists && (
                  <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-40 overflow-y-auto">
                    {therapists.length === 0 ? (
                      <p className="text-sm text-gray-400 p-3">Loading therapists...</p>
                    ) : (
                      therapists.map((t) => (
                        <label
                          key={t._id}
                          className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 cursor-pointer hover:bg-gray-50"
                        >
                          <input
                            type="checkbox"
                            checked={form.therapistIds.includes(t._id)}
                            onChange={() => toggleTherapist(t._id)}
                            className="rounded"
                          />
                          {`${t.name || ""} ${t.surname || ""}`.trim() || t.email}
                        </label>
                      ))
                    )}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreate}
                  disabled={submitting}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? "Creating..." : "Create Coupon"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCoupons;
